import React, { useEffect, useMemo, useState } from 'react';

import { proveedoresSancionadosData }
  from '../../json/proveedoresSancionadosData';

import './proveedoresSancionados.css';


const PAGE_SIZE = 50;


/* =========================================================
   NORMALIZAR TEXTO PARA BÚSQUEDA
========================================================= */

const normalizar = (texto = '') =>
  texto
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();


/* =========================================================
   MOSTRAR DATOS VACÍOS
========================================================= */

const mostrar = (valor) => {

  if (
    valor === null ||
    valor === undefined ||
    valor.toString().trim() === ''
  ) {
    return 'N/A';
  }

  return valor;
};


/* =========================================================
   COMPONENTE PRINCIPAL
========================================================= */

const ProveedoresSancionados = () => {

  const [busqueda, setBusqueda] = useState('');

  const [nivel, setNivel] = useState('');

  const [institucion, setInstitucion] = useState('');

  const [pagina, setPagina] = useState(1);

  const [seleccionado, setSeleccionado] = useState(null);


  /* =======================================================
     SUBIR AL INICIO AL ENTRAR
  ======================================================= */

  useEffect(() => {

    window.scrollTo(0, 0);

  }, []);


  /* =======================================================
     NIVELES DISPONIBLES
  ======================================================= */

  const niveles = useMemo(() => {

    return [
      ...new Set(
        proveedoresSancionadosData
          .map((item) => item.nivel)
          .filter(Boolean)
      ),
    ].sort((a, b) =>
      a.localeCompare(b, 'es')
    );

  }, []);


  /* =======================================================
     INSTITUCIONES DISPONIBLES
  ======================================================= */

  const instituciones = useMemo(() => {

    return [
      ...new Set(
        proveedoresSancionadosData
          .map(
            (item) =>
              item.institucionSancionadora
          )
          .filter(Boolean)
      ),
    ].sort((a, b) =>
      a.localeCompare(b, 'es')
    );

  }, []);


  /* =======================================================
     FILTROS + ORDENAR LOS ÚLTIMOS PRIMERO
  ======================================================= */

  const filtrados = useMemo(() => {

    const termino = normalizar(busqueda);

    return [...proveedoresSancionadosData]

      .filter((item) => {

        const coincideBusqueda =
          !termino ||

          normalizar(
            item.proveedorContratista
          ).includes(termino) ||

          normalizar(
            item.expediente
          ).includes(termino) ||

          normalizar(
            item.institucionSancionadora
          ).includes(termino) ||

          normalizar(
            item.sentidoResolucion
          ).includes(termino) ||

          normalizar(
            item.nivel
          ).includes(termino);


        const coincideNivel =
          !nivel ||
          item.nivel === nivel;


        const coincideInstitucion =
          !institucion ||
          item.institucionSancionadora ===
            institucion;


        return (
          coincideBusqueda &&
          coincideNivel &&
          coincideInstitucion
        );

      })

      .sort(
        (a, b) =>
          Number(b.numero || 0) -
          Number(a.numero || 0)
      );

  }, [
    busqueda,
    nivel,
    institucion,
  ]);


  /* =======================================================
     REGRESAR A PÁGINA 1 AL FILTRAR
  ======================================================= */

  useEffect(() => {

    setPagina(1);

  }, [
    busqueda,
    nivel,
    institucion,
  ]);


  /* =======================================================
     PAGINACIÓN
  ======================================================= */

  const totalPaginas = Math.max(
    1,
    Math.ceil(
      filtrados.length / PAGE_SIZE
    )
  );


  const inicio =
    (pagina - 1) * PAGE_SIZE;


  const visibles =
    filtrados.slice(
      inicio,
      inicio + PAGE_SIZE
    );


  /* =======================================================
     LIMPIAR FILTROS
  ======================================================= */

  const limpiarFiltros = () => {

    setBusqueda('');

    setNivel('');

    setInstitucion('');

    setPagina(1);

  };


  /* =======================================================
     PÁGINAS
  ======================================================= */

  const irPrimeraPagina = () => {

    setPagina(1);

  };


  const irPaginaAnterior = () => {

    setPagina((actual) =>
      Math.max(
        1,
        actual - 1
      )
    );

  };


  const irPaginaSiguiente = () => {

    setPagina((actual) =>
      Math.min(
        totalPaginas,
        actual + 1
      )
    );

  };


  const irUltimaPagina = () => {

    setPagina(totalPaginas);

  };


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <main className="proveedores-sancionados-page">

      <div className="proveedores-sancionados-container">


        {/* ===============================================
            ENCABEZADO
        =============================================== */}

        <header className="proveedores-sancionados-header">

          <p className="proveedores-sancionados-kicker">
            Secretaría de Contraloría
          </p>


          <h1>
            Proveedores y Contratistas
            Sancionados
          </h1>


          <div className="proveedores-sancionados-title-line">

            <span></span>

          </div>


          <p className="proveedores-sancionados-description">

            Consulta el registro de proveedores
            y contratistas sancionados del
            Gobierno del Estado de Hidalgo.

          </p>

        </header>


        {/* ===============================================
            BÚSQUEDA Y FILTROS
        =============================================== */}

        <section className="proveedores-sancionados-toolbar">


          {/* BUSCADOR */}

          <div className="proveedores-sancionados-search">

            <i className="fa-solid fa-magnifying-glass"></i>

            <input
              type="text"
              value={busqueda}
              onChange={(e) =>
                setBusqueda(
                  e.target.value
                )
              }
              placeholder="Buscar proveedor, contratista, expediente..."
            />

          </div>


          {/* NIVEL */}

          <div className="proveedores-sancionados-filter">

            <label>
              Nivel
            </label>

            <select
              value={nivel}
              onChange={(e) =>
                setNivel(
                  e.target.value
                )
              }
            >

              <option value="">
                Todos los niveles
              </option>


              {niveles.map((item) => (

                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>

              ))}

            </select>

          </div>


          {/* INSTITUCIÓN */}

          <div className="proveedores-sancionados-filter">

            <label>
              Institución sancionadora
            </label>

            <select
              value={institucion}
              onChange={(e) =>
                setInstitucion(
                  e.target.value
                )
              }
            >

              <option value="">
                Todas las instituciones
              </option>


              {instituciones.map((item) => (

                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>

              ))}

            </select>

          </div>


          {/* LIMPIAR */}

          <div className="proveedores-sancionados-actions">

            <button
              type="button"
              className="proveedores-sancionados-clear"
              onClick={limpiarFiltros}
            >

              <i className="fa-solid fa-filter-circle-xmark"></i>

              Limpiar

            </button>

          </div>

        </section>


        {/* ===============================================
            RESUMEN
        =============================================== */}

        <div className="proveedores-sancionados-summary">

          <div>

            <strong>
              {filtrados.length}
            </strong>

            <span>
              registros encontrados
            </span>

          </div>


          <div className="proveedores-sancionados-page-size">

            <i className="fa-solid fa-table-list"></i>

            50 registros por página

          </div>

        </div>


        {/* ===============================================
            TABLA
        =============================================== */}

        <section className="proveedores-sancionados-table-wrapper">

          <table className="proveedores-sancionados-table">

            <thead>

              <tr>

                <th>
                  No.
                </th>

                <th>
                  Nivel
                </th>

                <th>
                  Proveedor o contratista
                </th>

                <th>
                  Institución sancionadora
                </th>

                <th>
                  Expediente
                </th>

                <th>
                  Resolución
                </th>

                <th>
                  Sanción
                </th>

              </tr>

            </thead>


            <tbody>

              {visibles.length > 0 ? (

                visibles.map((item) => (

                  <tr
                    key={`${item.numero}-${item.expediente}`}
                  >


                    {/* NÚMERO */}

                    <td
                      data-label="No."
                      className="proveedores-sancionados-number"
                    >

                      {mostrar(
                        item.numero
                      )}

                    </td>


                    {/* NIVEL */}

                    <td data-label="Nivel">

                      <span className="proveedores-sancionados-level">

                        {mostrar(
                          item.nivel
                        )}

                      </span>

                    </td>


                    {/* PROVEEDOR */}

                    <td data-label="Proveedor o contratista">

                      <strong>

                        {mostrar(
                          item.proveedorContratista
                        )}

                      </strong>

                    </td>


                    {/* INSTITUCIÓN */}

                    <td data-label="Institución sancionadora">

                      {mostrar(
                        item.institucionSancionadora
                      )}

                    </td>


                    {/* EXPEDIENTE */}

                    <td data-label="Expediente">

                      <span className="proveedores-sancionados-expediente">

                        {mostrar(
                          item.expediente
                        )}

                      </span>

                    </td>


                    {/* RESOLUCIÓN */}

                    <td data-label="Resolución">

                      <button
                        type="button"
                        className="proveedores-sancionados-detail"
                        onClick={() =>
                          setSeleccionado(item)
                        }
                      >

                        Ver detalle

                        <i className="fa-solid fa-arrow-right"></i>

                      </button>

                    </td>


                    {/* PDF */}

                    <td data-label="Sanción">

                      {item.pdfUrl ? (

                        <a
                          href={item.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="proveedores-sancionados-pdf"
                        >

                          <i className="fa-solid fa-file-pdf"></i>

                          Ver sanción

                        </a>

                      ) : (

                        <span className="proveedores-sancionados-no-pdf">

                          Sin documento

                        </span>

                      )}

                    </td>

                  </tr>

                ))

              ) : (

                <tr>

                  <td
                    colSpan="7"
                    className="proveedores-sancionados-empty"
                  >

                    <i className="fa-solid fa-magnifying-glass"></i>

                    No se encontraron registros
                    con los filtros seleccionados.

                  </td>

                </tr>

              )}

            </tbody>

          </table>

        </section>


        {/* ===============================================
            PAGINACIÓN
        =============================================== */}

        <div className="proveedores-sancionados-pagination">


          <button
            type="button"
            onClick={irPrimeraPagina}
            disabled={pagina === 1}
          >

            <i className="fa-solid fa-angles-left"></i>

            Primera

          </button>


          <button
            type="button"
            onClick={irPaginaAnterior}
            disabled={pagina === 1}
          >

            <i className="fa-solid fa-chevron-left"></i>

            Anterior

          </button>


          <span>

            Página

            {' '}

            <strong>
              {pagina}
            </strong>

            {' '}de{' '}

            <strong>
              {totalPaginas}
            </strong>

          </span>


          <button
            type="button"
            onClick={irPaginaSiguiente}
            disabled={
              pagina === totalPaginas
            }
          >

            Siguiente

            <i className="fa-solid fa-chevron-right"></i>

          </button>


          <button
            type="button"
            onClick={irUltimaPagina}
            disabled={
              pagina === totalPaginas
            }
          >

            Última

            <i className="fa-solid fa-angles-right"></i>

          </button>

        </div>


        {/* ===============================================
            AVISO
        =============================================== */}

        <div className="proveedores-sancionados-notice">

          <i className="fa-solid fa-circle-info"></i>

          <p>

            La información presentada corresponde
            al registro de proveedores y
            contratistas sancionados.

          </p>

        </div>

      </div>


      {/* ===================================================
          MODAL DE DETALLE
      =================================================== */}

      {seleccionado && (

        <div
          className="proveedores-sancionados-modal-overlay"
          onClick={() =>
            setSeleccionado(null)
          }
        >

          <div
            className="proveedores-sancionados-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >


            {/* HEADER MODAL */}

            <div className="proveedores-sancionados-modal-header">

              <div>

                <span>

                  Registro No.{' '}

                  {seleccionado.numero}

                </span>


                <h2>

                  {mostrar(
                    seleccionado.proveedorContratista
                  )}

                </h2>

              </div>


              <button
                type="button"
                className="proveedores-sancionados-modal-close"
                onClick={() =>
                  setSeleccionado(null)
                }
                aria-label="Cerrar"
              >

                <i className="fa-solid fa-xmark"></i>

              </button>

            </div>


            {/* CUERPO MODAL */}

            <div className="proveedores-sancionados-modal-body">


              <Detalle
                label="Nivel"
                value={
                  seleccionado.nivel
                }
              />


              <Detalle
                label="Número de expediente"
                value={
                  seleccionado.expediente
                }
              />


              <Detalle
                label="Institución sancionadora"
                value={
                  seleccionado.institucionSancionadora
                }
              />


              <Detalle
                label="Sentido de la resolución"
                value={
                  seleccionado.sentidoResolucion
                }
                full
              />


              {seleccionado.pdfUrl && (

                <div className="proveedores-sancionados-modal-pdf">

                  <a
                    href={
                      seleccionado.pdfUrl
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >

                    <i className="fa-solid fa-file-pdf"></i>

                    Abrir documento de sanción

                  </a>

                </div>

              )}

            </div>

          </div>

        </div>

      )}

    </main>

  );

};


/* =========================================================
   COMPONENTE DETALLE
========================================================= */

const Detalle = ({
  label,
  value,
  full = false,
}) => {

  return (

    <div
      className={
        `proveedores-sancionados-detail-item ${
          full
            ? 'proveedores-sancionados-detail-full'
            : ''
        }`
      }
    >

      <span>
        {label}
      </span>

      <p>
        {mostrar(value)}
      </p>

    </div>

  );

};


export default ProveedoresSancionados;